import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import {
  ONLY_NUMBERS_RULE,
  PERSIAN_LATTER_RULE,
} from '@/common/components/base/input/textInput/textInput.rules';
import { atFaultDriverQueryOptions } from '@/common/queries/selections/atFaultDriver/atFaultDriverQueryOptions';
import usePersonalInquiryStore from '@/common/store/personalInquiry/usePersonalInquiry';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_CULPRIT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import PersonalInquiry from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry';
import { GetCulpritDriverInfoFormType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/getculpritDriverInfo/getCulpritDriverInfoQuery.type';
import { usePatchCulpritPersonalInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/patchCulpritPersonalInformation/patchCulpritPersonalInformationQueryOptions';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const InitialCulpritDriverInfo = () => {
  const { id } = useParams();
  const { data, clearInquiryData } = usePersonalInquiryStore();
  const navigate = useNavigate();
  const { data: driverInfo } = useQuery(atFaultDriverQueryOptions());
  const { mutate: patchPersonalInformation, isPending: loading } =
    usePatchCulpritPersonalInformationQueryOptions();
  const methods = useFormContext<GetCulpritDriverInfoFormType>();
  const onSubmit = (formData: GetCulpritDriverInfoFormType) => {
    const data = {
      ...formData.personal_information,
      at_fault_driver: formData?.personal_information?.at_fault_driver?.value,
      gender: formData?.personal_information?.gender,
      birthdate: convertPersianToGregorian(
        formData?.personal_information?.birthdate?.toString() ?? '',
      ),
    };
    patchPersonalInformation(
      {
        data: {
          ...data,
          identity_issue_location:
            data?.identity_issue_location !== ''
              ? data?.identity_issue_location
              : null,
          identity_number:
            data?.identity_number !== '' ? data?.identity_number : null,
          father_name: data?.father_name !== '' ? data?.father_name : null,
        },
        id: +id!,
      },
      {
        onSuccess: () => {
          (navigate(
            ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_INFORMATION({
              id: +id!,
            }).DRIVER_OWNER_INFORMATION.certificate,
            { replace: true },
          ),
            clearInquiryData());
        },
      },
    );
  };
  const formatBirthDate = (dateStr?: string) => {
    if (!dateStr) return '';
    let year = dateStr.substring(0, 4);
    let month = dateStr.substring(4, 6);
    let day = dateStr.substring(6, 8);
    return `${year}/${month}/${day}`;
  };

  useEffect(() => {
    if (data?.national_code) {
      methods.reset({
        personal_information: {
          ...data,
          birthdate: data?.birthdate ? formatBirthDate(data?.birthdate) : '',
          gender: data?.gender === 'MALE' ? 'male' : 'female',
          first_name: data?.first_name,
          last_name: data?.last_name,
          driver_iranian: true,
          identity_number: String(data?.identification_number),
          identity_issue_location: data?.birth_place,
        },
      });
    }
  }, [data]);

  return (
    <Flex direction="column" gap={8}>
      <Box py={24}>
        <PersonalInquiry outSideForm={useFormContext} />
      </Box>
      <ModalInput
        name="personal_information.at_fault_driver"
        label="راننده مقصر"
        options={driverInfo || []}
      />
      <RadioGroup
        title="جنسیت راننده"
        name="personal_information.gender"
        options={[
          { label: 'آقا', value: 'male' },
          { label: 'خانم', value: 'female' },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ paddingBottom: '24px', gap: '2rem' })}
      />
      <Box>
        <TextInput
          label="نام"
          name="personal_information.first_name"
          placeholder="نام را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <TextInput
          label="نام ‌خانوادگی"
          name="personal_information.last_name"
          placeholder="نام ‌خانوادگی را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <DateInput
          label="تاریخ تولد"
          name="personal_information.birthdate"
          buttonText="انتخاب تاریخ"
          rules={{ validate: validateNotFutureDate, required: requiredText }}
          modalLabel="انتخاب تاریخ تولد"
          isDefaultValueEmpty
        />
      </Box>

      <RadioGroup
        title="تابعیت"
        name="personal_information.driver_iranian"
        options={[
          { label: 'ایرانی', value: true },
          { label: 'غیر ایرانی', value: false },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ gap: '2rem' })}
      />

      <Box>
        <TextInput
          label="کدملی"
          name="personal_information.national_code"
          placeholder="کدملی را وارد کنید"
          defaultValue=""
          rules={{
            required: requiredText,
            ...ONLY_NUMBERS_RULE,
            ...NATIONAL_CODE_RULE,
          }}
          max={10}
        />
      </Box>
      <Box>
        <TextInput
          label="نام پدر"
          name="personal_information.father_name"
          placeholder="نام پدر را وارد کنید"
          defaultValue=""
          optionalLabel
        />
      </Box>
      <Box>
        <TextInput
          label="شماره شناسنامه"
          name="personal_information.identity_number"
          placeholder="شماره شناسنامه را وارد کنید"
          defaultValue=""
          optionalLabel
          noCharInput
          max={10}
        />
      </Box>
      <Box>
        <TextInput
          label="محل صدور شناسنامه"
          name="personal_information.identity_issue_location"
          placeholder="محل صدور شناسنامه را وارد کنید"
          optionalLabel
          defaultValue=""
        />
      </Box>

      <RadioGroup
        title="آیا راننده مالک وسیله‌نقلیه است ؟"
        name="personal_information.is_owner_driver"
        options={[
          { label: 'بله', value: true },
          { label: 'خیر', value: false },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ paddingBottom: '24px', gap: '2rem' })}
      />

      <Box w="100%" my="1rem">
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Box>
    </Flex>
  );
};

export default InitialCulpritDriverInfo;
